import { shouldExcludeFromPrefetch } from "@theme/pjax-routing";
import { requestIdleCallback } from "@theme/utilities";

// Warms pjax-max's cache ahead of clicks, by each link's data-prefetch:
//   (none)        when the link scrolls into view (the default)
//   "immediate"   as soon as it's seen
//   "hover"       pjax-max's own: on pointer-over and keyboard focus
//   "critical"    pjax-max's own: once the browser is idle after each page
//   "none"        never
// Pages go through core.prefetch(), so a click on a link still prefetching
// waits for that request instead of sending another, and the cache's bound
// and freshness (pjax-init.js) apply. Without the router (page transitions
// off, or the theme editor) nothing is prefetched, since nothing would use it.
//
// core.prefetch() can't ask for a low fetch priority, so prefetching starts
// once the page has loaded, and links that scroll into view are fetched when
// the browser is idle: they shouldn't compete with the page's own images.

class LinkPrefetcher {
  #core;
  #observed = new Set();
  #queued = new Set();
  #flushing = false;
  #intersectionObserver;

  constructor(core) {
    this.#core = core;
    this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, {
      rootMargin: "0px",
      threshold: 0,
    });

    this.#scan(document);

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) this.#scan(node);
        }
      }
    }).observe(document.body, { subtree: true, childList: true });

    // A link observed in a view that has since been swapped out would keep
    // that whole view alive; let them go once a navigation has ended.
    core.addEventListener("NAVIGATE_END", () => {
      for (const link of this.#observed) {
        if (!link.isConnected) this.#unobserve(link);
      }
    });
  }

  #mode(el) {
    return (el.getAttribute("data-prefetch") || "viewport").toLowerCase();
  }

  #scan(root) {
    const handle = (el) => {
      if (!this.#isCandidate(el)) return;
      const mode = this.#mode(el);
      if (mode === "immediate") {
        this.#core.prefetch(el);
      } else if (mode === "viewport" && !this.#observed.has(el)) {
        this.#observed.add(el);
        this.#intersectionObserver.observe(el);
      }
    };

    if (root.matches?.("a[href]")) handle(root);
    root.querySelectorAll?.("a[href]").forEach(handle);
  }

  #isCandidate(el) {
    if (el.target === "_blank") return false;
    if (el.hasAttribute("download") || el.closest("[data-router-disabled]")) return false;
    const raw = el.getAttribute("href") ?? "";
    if (!raw || raw.startsWith("#")) return false;
    if (/^(mailto:|tel:|javascript:)/i.test(raw)) return false;
    if (shouldExcludeFromPrefetch(el.href)) return false;
    return true;
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      this.#unobserve(entry.target);
      this.#queued.add(entry.target);
    }
    if (!this.#queued.size || this.#flushing) return;
    this.#flushing = true;
    requestIdleCallback(
      () => {
        this.#flushing = false;
        for (const link of this.#queued) if (link.isConnected) this.#core.prefetch(link);
        this.#queued.clear();
      },
      { timeout: 2000 }
    );
  };

  #unobserve(link) {
    this.#intersectionObserver.unobserve(link);
    this.#observed.delete(link);
  }
}

const start = (core) => {
  if (navigator.connection?.saveData) return;
  if (document.readyState === "complete") new LinkPrefetcher(core);
  else window.addEventListener("load", () => new LinkPrefetcher(core), { once: true });
};

if (window.__pjax) {
  start(window.__pjax);
} else {
  document.addEventListener("pjax:ready", (event) => start(event.detail.pjax), { once: true });
}
